import {
  Component,
  ElementRef,
  EventEmitter,
  HostBinding,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { Column } from '@miq/catalog';
import { ControlValueAccessorTemplate } from './control-value-accessor-template';
import { DisabledFormStateService } from './disable-form-state.service';
import { UntilDestroy } from '@ngneat/until-destroy';

/**
 * Intended to be the central component to concentrate the business logic
 * and connect store, dispatch and react to selectors at input level
 */
@UntilDestroy()
@Component({
  selector: 'miq-input-control',
  template: `
    <div class="input-container">
      <miq-advanced-input
        [(currentValue)]="currentValue"
        [config]="column | toAdvancedInputConfig"
      ></miq-advanced-input>
    </div>
  `,
  styleUrls: ['./input-control.component.scss'],
  standalone: false,
})
export class InputControlComponent extends ControlValueAccessorTemplate implements OnInit, OnDestroy {
  @HostBinding('class.hidden') @Input('hidden') isHidden = false;
  @HostBinding('class.miq-form-input') customVariant = true;

  /**
   * Column def
   */
  private _column: Column = null;

  @Input() set column(c: Column) {
    this._column = c;
    this.setDisabledState(c.isDisabled);
  }

  public get column() {
    return this._column;
  }

  @Output() columnChange = new EventEmitter<Column>();

  private _currentValue: any;
  public get currentValue(): any {
    return this._currentValue;
  }

  /**
   * set current control value, only if the new value is different to the
   * current value.
   *
   * @param val New value
   */
  public set currentValue(val: any) {
    // to fire mark as touched
    this.OnTouchedFn(this);

    this._currentValue = val;
    // to fire valueChanges
    this.OnChangeFn(this._currentValue);
  }

  /**
   *
   * @param ngControlDir
   * @param el
   * @param disabledStateService
   */
  constructor(
    @Optional() @Self() ngControlDir: NgControl,
    el: ElementRef,
    private disabledStateService: DisabledFormStateService
  ) {
    super(ngControlDir, el);
  }

  ngOnDestroy(): void {
    // required by untilDestroy
    super.ngOnDestroy();
  }

  /**
   * CVA Communication method. Simply writes/stores the new input value as the
   * new current value.
   */
  writeValue(newValue: any): void {
    this._currentValue = newValue;
  }

  /**
   * CVA Communication method
   * @param isColDisabled
   */
  setDisabledState?(isColDisabled: boolean): void {
    if (this._column == null) return;
    const nextColumn = this.disabledStateService.setDisabled(this.column, isColDisabled);
    const shouldEmit = this._column?.isDisabled !== nextColumn?.isDisabled;
    this._column = nextColumn;
    if (shouldEmit) this.columnChange.emit(this._column);
  }
}
